import { PAYMENT_GATEWAY, type PaymentGateway } from '../../types/enums';
import type { PaymentProvider } from '../../types/payment';
import { useMarketplaceConfig } from '../../queries/marketplace';
import { pay2LocalPaymentProviders } from '../../config/payment/pay2local';
import { ligdiCashPaymentProviders } from '../../config/payment/ligdicash';
import { cryptoPaymentProviders } from '../../config/payment/crypto';
import { finpspPaymentProviders } from '../../config/payment/finpsp';
import { resolveCryptoCashierConfig } from './resolveCryptoCashierConfig';

type GatewayConfig = {
  paymentProviders: PaymentProvider[];
  requiresMethodBeforeForm: boolean;
  withdrawalEnabled: boolean;
};

export const usePaymentGateway = () => {
  const { data: marketplaceData } = useMarketplaceConfig();
  const marketplaceConfig = marketplaceData!.marketplaceConfig;
  const { paymentGateway } = marketplaceConfig;
  const cryptoCashier = resolveCryptoCashierConfig(marketplaceConfig);

  // Selection is a gateway capability; an empty method list must not make it optional.
  const gatewayDataMap: Record<PaymentGateway, GatewayConfig> = {
    [PAYMENT_GATEWAY.KAPOPAY]: {
      paymentProviders: [],
      requiresMethodBeforeForm: false,
      withdrawalEnabled: true,
    },
    [PAYMENT_GATEWAY.PAY_2_LOCAL]: {
      paymentProviders: pay2LocalPaymentProviders,
      requiresMethodBeforeForm: true,
      withdrawalEnabled: true,
    },
    [PAYMENT_GATEWAY.LIGDICASH]: {
      paymentProviders: ligdiCashPaymentProviders,
      requiresMethodBeforeForm: true,
      withdrawalEnabled: true,
    },
    [PAYMENT_GATEWAY.CRYPTO]: {
      paymentProviders: cryptoPaymentProviders,
      // Crypto providers define selection requirements even when their catalog is empty.
      requiresMethodBeforeForm: cryptoCashier.requiresMethodBeforeForm,
      withdrawalEnabled: cryptoCashier.withdrawalEnabled,
    },
    [PAYMENT_GATEWAY.FINPSP]: {
      paymentProviders: finpspPaymentProviders,
      requiresMethodBeforeForm: true,
      withdrawalEnabled: true,
    },
  };

  const gatewayContext = gatewayDataMap[paymentGateway];

  return {
    ...gatewayContext,
    paymentGateway,
    cryptoCashier,
  };
};
